/* eslint-disable no-console */
const { formatMoney } = window.BAO.Utils

class PersonalisationForm extends window.BAO.CustomElement() {
  constructor () {
    super()

    this.setPropertyValue = this.setPropertyValue.bind(this)
    this.createOverlayElements = this.createOverlayElements.bind(this)
    this.setServiceProductInputs = this.setServiceProductInputs.bind(this)

    this.regex = /^[a-zA-Z0-9&.,\-/"'*°❤⭐  \r\n]*$/

    this.uploadInput = this.querySelector(
      '[data-personalisation-file-upload-el="input"]',
    )

    this.uploadError = this.els.uploadError?.element
  }

  connectedCallback () {
    super.connectedCallback()

    try {
      const data = JSON.parse(this.els.data.element.innerText)
      this.productProps = data.product
      this.parentProductProps = data.parentProduct
      this.isServiceProduct = data.isServiceProduct
      this.forceCapitals = data.forceCapitals
      this.personalisationProps = data.placements
    } catch {
      console.error('Error parsing personalisation props')
      return
    }

    const selectedParentId = document.querySelector(
      '[data-personalisation-tabs-external-el="form"] input[name="id"]',
    ).value
    this.selectedParentVariant = this.parentProductProps.variants.find(
      (variant) => variant.id === parseInt(selectedParentId),
    )

    if (this.isServiceProduct) {
      this.selectedVariant = this.findMatchingVariant(
        this.selectedParentVariant,
        this.productProps,
      )
    } else {
      this.selectedVariant = this.productProps.variants.find(
        (variant) => variant.id === parseInt(this.els.variantId.element.value),
      )
    }

    this.createOverlayElements()
    this.updatePrices()
  }

  setupListeners () {
    this.els.textInput?.elements.forEach((el) => {
      this.listeners.add(el, 'keydown', this.onKeyDown.bind(this))
      this.listeners.add(el, 'keyup', this.onInputChange.bind(this))
      this.listeners.add(el, 'paste', this.onInputPaste.bind(this))
      this.listeners.add(el, 'change', this.onInputChange.bind(this))
    })

    this.els.fontStyleOption?.elements.forEach((el) => {
      this.listeners.add(
        el,
        'change',
        this.handleFontStyleButtonChange.bind(this),
      )
    })

    this.externalEls.fontSizeRadio?.elements.forEach((el) => {
      this.listeners.add(
        el,
        'change',
        this.handleFontSizeButtonChange.bind(this),
      )
    })

    this.externalEls.pillRadio?.elements
      .filter((el) => el.dataset.engravingType === this.dataset.engravingType)
      .forEach((el) => {
        this.listeners.add(el, 'change', this.handlePillChange.bind(this))
      })

    this.els.pawInput?.elements.forEach((el) => {
      this.listeners.add(el, 'change', this.handlePawInput.bind(this))
    })

    this.els.cropPreview?.elements.forEach((el) => {
      this.listeners.add(el, 'change', this.handleUploadInput.bind(this))
    })

    this.els.inspireMe?.elements.forEach((el) => {
      this.listeners.add(el, 'click', this.handleInspireMeClick.bind(this))
    })

    this.externalEls.quote?.elements.forEach((el) => {
      this.listeners.add(el, 'click', this.handleQuoteClick.bind(this))
    })

    this.listeners.add(
      this.els.addToCart.element,
      'click',
      this.handleATCClick.bind(this),
    )

    if (this.els?.termsCheckbox?.exists) {
      this.listeners.add(
        this.els.termsCheckbox.element,
        'change',
        this.handleTermsCheckbox.bind(this),
      )
    }

    this.els.viewImage?.elements.forEach((viewImage) => {
      this.listeners.add(
        viewImage,
        'click',
        this.handleViewImageClick.bind(this),
      )
    })

    this.els.emojiButton?.elements.forEach((emojiButton) => {
      this.listeners.add(
        emojiButton,
        'click',
        this.handleEmojiClick.bind(this),
      )
    })

    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PDP.VARIANT_CHANGED,
      this.handleVariantChange.bind(this),
    )

    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PDP.OPTION_CHANGED,
      this.handleOptionChange.bind(this),
    )

    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PDP.PRICE_CHANGE,
      () => this.updatePrices(),
    )

    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PDP.CLEAN_UP,
      this.handleErrors.bind(this),
    )
  }

  onInputPaste (e) {
    window.setTimeout(() => {
      e.target.dispatchEvent(new Event('change'))
    }, 1)
  }

  handleEmojiClick (e) {
    e.preventDefault()

    const button = e.currentTarget

    const textInput = button
      .closest('.prd-Personalisation_EmojiInput')
      .querySelector('[data-personalisation-form-el="textInput"]')

    if (!textInput) return

    textInput.value = `${textInput.value}${button.dataset.value}`

    textInput.dispatchEvent(new Event('change'))
  }

  handleInspireMeClick (e) {
    e.preventDefault()

    const drawer = document.querySelector('[key="personalisation-quotes"]')
    const placement = e.currentTarget.closest(
      '[data-personalisation-form-el="placement"]',
    )

    drawer.dataset.engravingType = this.dataset.engravingType
    drawer.dataset.placement = placement.dataset.placement

    const props = this.personalisationProps.find(
      (props) => props.placement === placement.dataset.placement,
    )

    this.externalEls.quote?.elements.forEach((quote) => {
      const quoteText = quote.dataset.text
      quote.setAttribute(
        'aria-hidden',
        quoteText.length >= props.maxCharacters,
      )
    })
  }

  handleQuoteClick (e) {
    e.preventDefault()

    const drawer = document.querySelector('[key="personalisation-quotes"]')

    const engravingType = drawer.dataset.engravingType
    const placement = drawer.dataset.placement

    if (engravingType !== this.dataset.engravingType) return

    const button = e.currentTarget

    const quoteTarget = this.getPlacementInput('textInput', placement)

    if (!quoteTarget || !button) return

    quoteTarget.value = button.innerText

    quoteTarget.dispatchEvent(new Event('change'))

    window.drawers.closeDrawer()
  }

  onKeyDown (e) {
    const { target, key, keyCode } = e

    if (target.hasAttribute('data-disable-numbers')) {
      const allowedKeys = [
        'Backspace',
        'Delete',
        'ArrowLeft',
        'ArrowRight',
        'ArrowUp',
        'ArrowDown',
        'Tab',
        'Enter',
        'Escape',
        'Home',
        'End',
      ]
      const isNumber = /^[0-9]$/.test(key)

      if (isNumber && !e.ctrlKey && !e.metaKey && !allowedKeys.includes(key)) {
        e.preventDefault()
        return false
      }
    }

    if (keyCode !== 13) return true

    const maxLines = parseInt(target.rows)

    if (target.value.split('\n').length >= maxLines) {
      e.preventDefault()
      return false
    }

    return true
  }

  onInputChange (e) {
    const { target } = e

    if (target.hasAttribute('data-disable-numbers')) {
      target.value = target.value.replace(/[0-9]/g, '')
    }

    if (this.forceCapitals) {
      target.value = target.value.toUpperCase()
    }

    if (target.value.length > target.maxLength) {
      target.value = target.value.substring(0, target.maxLength)
    }

    const maxLines = parseInt(target.rows)

    if (target.value.split('\n').length > maxLines) {
      target.value = target.value.split('\n').slice(0, maxLines).join('\n')
    }

    this.els.maxLength.element.setAttribute(
      'aria-hidden',
      !(target.maxLength > 10 && target.value.length >= target.maxLength),
    )

    const hasError = !target.value.match(this.regex)

    target
      .closest('.prd-Personalisation_InputContainer')
      .querySelector('[data-personalisation-form-el="error"]')
      .setAttribute('aria-hidden', !hasError)

    if (target.value) {
      this.els.valueError?.element.setAttribute('aria-hidden', true)
    }

    if (!this.hasError) {
      this.setPropertyValue(
        target.closest('[data-personalisation-form-el="placement"]').dataset
          .placement,
        'text',
        target.value,
      )
    }

    const matchingPill = this.externalEls.pillRadio?.elements.find(
      (el) =>
        el.dataset.engravingType === this.dataset.engravingType &&
        el.dataset.placement ===
          target.closest('[data-personalisation-form-el="placement"]').dataset
            .placement,
    )

    if (matchingPill) matchingPill.click()
  }

  createHiddenInput (name, value, options, parent) {
    const input = document.createElement('input')
    input.type = 'hidden'
    input.name = name
    input.value = value
    Object.entries(options).forEach(([key, value]) => {
      input.setAttribute(key, value)
    })
    parent.appendChild(input)

    return input
  }

  handlePawHiddenInputs (currentTarget) {
    const controls = currentTarget.getAttribute('aria-controls')

    this.querySelectorAll('[data-paw-input]').forEach((input) =>
      input.remove(),
    )

    if (controls === 'addPad') {
      this.createHiddenInput(
        'items[2][id]',
        currentTarget.dataset.pawProductId,
        { 'data-paw-input': 'true' },
        this.querySelector('form'),
      )
      this.createHiddenInput(
        'items[2][parent_id]',
        this.els.variantId?.element?.value,
        { 'data-paw-input': 'true' },
        this.querySelector('form'),
      )

      const uploadTabs = this.querySelector(
        '.prd-Personalisation_UploadTabButtons',
      )
      if (uploadTabs) {
        this.createHiddenInput(
          'properties[custcol_dd_adventure]',
          'Pending',
          { id: 'paw-print-pending' },
          uploadTabs,
        )
      }
    } else {
      this.querySelector('#paw-print-pending')?.remove()
    }
  }

  handlePawInput (e) {
    const { currentTarget } = e
    const controls = currentTarget.getAttribute('aria-controls')

    const uploadElement = this.querySelector('personalisation-file-upload')
    if (uploadElement) {
      uploadElement.ariaHidden = controls === 'addPad'
    }

    this.handlePawHiddenInputs(currentTarget)
    this.updateAddToCartButton()
    this.updatePrices()
  }

  handleUploadInput (e) {
    // Update visualiser with the image
    const { target } = e

    const file = target.value

    const placement = target.closest(
      '[data-personalisation-form-el="placement"]',
    ).dataset.placement

    this.setPropertyValue(placement, 'image', file)

    this.updateAddToCartButton.bind(this)
  }

  handleFontStyleButtonChange (e) {
    const { currentTarget } = e

    this.personalisationProps.forEach((prop) => {
      this.setPropertyValue(prop.placement, 'fontFamily', currentTarget.value)
    })
  }

  handleFontSizeButtonChange (e) {
    const { currentTarget } = e

    const parentDrawer = currentTarget.closest(
      '[data-personalisation-form-external-el="font-sizes-drawer"]',
    )

    if (
      !parentDrawer ||
      parentDrawer.dataset.engravingType !== this.dataset.engravingType
    ) {
      return
    }

    const fontSize = parseInt(currentTarget.dataset.fontSize)
    const maxCharacters = parseInt(currentTarget.dataset.maxCharacters)
    const maxCharactersAlt = parseInt(currentTarget.dataset.maxCharactersAlt)
    const maxLines = parseInt(currentTarget.dataset.maxLines || 1)

    this.els.textInput.elements.forEach((textInput) => {
      const placement = textInput.closest(
        '[data-personalisation-form-el="placement"]',
      )
      const useAltLimit = placement.dataset.useAltLimit === 'true'
      const characterLimit = useAltLimit ? maxCharactersAlt : maxCharacters

      textInput.maxLength = characterLimit
      textInput.rows = maxLines

      let trimmedText = textInput.value

      if (trimmedText.length > characterLimit) {
        trimmedText = textInput.value.substring(0, characterLimit)
      }

      if (trimmedText.split('\n').length > maxLines) {
        trimmedText = trimmedText.split('\n').slice(0, maxLines).join('\n')
      }

      if (trimmedText !== textInput.value) {
        textInput.value = trimmedText

        const placement = textInput.closest(
          '[data-personalisation-form-el="placement"]',
        ).dataset.placement

        this.setPropertyValue(placement, 'text', trimmedText)
      }
    })

    this.els.maxCharCount.elements.forEach((maxChars) => {
      const placement = maxChars.closest(
        '[data-personalisation-form-el="placement"]',
      )
      const useAltLimit = placement.dataset.useAltLimit === 'true'
      const characterLimit = useAltLimit ? maxCharactersAlt : maxCharacters

      maxChars.innerText = characterLimit
    })

    this.els.fontSizeValue.elements.forEach((textValue) => {
      textValue.innerText = currentTarget.value
    })

    this.els.fontSizeInput.elements.forEach((fontSizeInput) => {
      fontSizeInput.value = currentTarget.value
    })

    this.externalEls.maxCharCount.elements.forEach((maxCharsMedia) => {
      if (
        !maxCharsMedia.closest(
          `[data-engraving-type="${this.dataset.engravingType}"]`,
        )
      ) {
        return
      }

      const placement = maxCharsMedia.closest(
        '[data-personalisation-form-external-el="placementMediaContainer"]',
      )
      const useAltLimit = placement.dataset.useAltLimit === 'true'
      const characterLimit = useAltLimit ? maxCharactersAlt : maxCharacters

      maxCharsMedia.innerText = characterLimit
    })

    this.personalisationProps.forEach((prop) => {
      this.setPropertyValue(prop.placement, 'fontSize', fontSize)
    })

    window.drawers.closeDrawer()
  }

  handlePillChange (e) {
    const { currentTarget } = e

    document
      .querySelectorAll(
        `[data-personalisation-form-external-el="placementMediaContainer"][data-engraving-type="${this.dataset.engravingType}"]`,
      )
      .forEach((container) => {
        container.setAttribute(
          'aria-hidden',
          container.dataset.placement !== currentTarget.value,
        )
      })
  }

  handleOptionChange (e) {
    if (e?.detail?.stone && e.detail.stone !== this.stone) {
      this.personalisationProps.forEach((prop) => {
        this.setPropertyValue(prop.placement, 'stoneType', e.detail.stone)
      })
    }
  }

  handleVariantChange (e) {
    const parentVariant = e.detail?.variant?.data

    if (!parentVariant) return

    const matchingVariant = this.findMatchingVariant(
      parentVariant,
      this.productProps,
    )

    if (!matchingVariant) return

    this.selectedParentVariant = parentVariant
    this.selectedVariant = matchingVariant

    this.updateVariantId(parentVariant, matchingVariant)
    this.updatePlacementVisibility(parentVariant)
    this.updateAvailabilityState(parentVariant, matchingVariant)
    this.updateStatusContainers(parentVariant, matchingVariant)
    this.updatePrices()
  }

  updatePlacementVisibility (parentVariant) {
    const placements = this.querySelectorAll(
      '[data-personalisation-form-el="placement"]',
    )

    placements.forEach((placement) => {
      const compatibleTitles = placement.dataset.placementTitle
      const placementName = placement.dataset.placement
      const isCompatible =
        !compatibleTitles || compatibleTitles.includes(parentVariant.title)

      placement.setAttribute('aria-hidden', !isCompatible)

      if (!isCompatible) {
        const textInput = this.getPlacementInput('textInput', placementName)
        if (textInput) {
          textInput.value = ''
        }

        const props = this.personalisationProps.find(
          (p) => p.placement === placementName,
        )
        if (props) {
          props.text = ''
          props.image = null
          this.updatePreviewText(placementName, props)
          this.updateLimitCounts(placementName, props)
          this.updateOverlay(props)
        }
      }
    })
  }

  findMatchingVariant (variant, product) {
    if (this.isServiceProduct) {
      // Try to match service variant by parent's option values
      const optionMatch = product.variants.find((v) => {
        return (
          v.option1 === variant.option1 &&
          v.option2 === variant.option2 &&
          v.option3 === variant.option3
        )
      })

      if (optionMatch) {
        return optionMatch
      }

      // Fallback: first available service variant
      const availableVariant = product.variants.find((v) => v.available)

      if (availableVariant) {
        return availableVariant
      }

      // Last resort: first variant (preserves original fallback)
      return product.variants[0]
    }

    const matchingVariant = product.variants.find((v) => {
      return (
        v.option1 === variant.option1 &&
        v.option2 === variant.option2 &&
        v.option3 === variant.option3
      )
    })

    return matchingVariant
  }

  updateVariantId (parentVariant, variant) {
    this.els.variantId.element.value = this.isServiceProduct
      ? parentVariant.id
      : variant.id

    if (this.isServiceProduct && this.els.serviceParentId?.element) {
      this.els.serviceParentId.element.value = parentVariant.id
    }
  }

  updateAvailabilityState (parentVariant, variant) {
    const available = this.isServiceProduct
      ? parentVariant.available && variant.available
      : variant.available

    this.querySelector('[data-product-form]').dataset.productAvailable =
      available

    this.updateAddToCartButton()
  }

  updateStatusContainers (parentVariant, variant) {
    const available = this.isServiceProduct
      ? parentVariant.available && variant.available
      : variant.available

    this.els.status.element.setAttribute('data-available', available)

    this.els.statusValue?.elements.forEach((el) => {
      const variantToCompare = this.isServiceProduct ? parentVariant : variant
      const value = variantToCompare[`option${el.dataset.option}`]
      if (value) {
        el.innerText = value
      }
    })
  }

  updatePrices () {
    const parentVariant = this.selectedParentVariant
    const variant = this.selectedVariant

    const parentProductPrice = parentVariant.price
    const engravingProductPrice = variant.price

    let priceDifference = engravingProductPrice - parentProductPrice
    let priceTotal = engravingProductPrice

    if (this.isServiceProduct) {
      priceDifference = engravingProductPrice
      priceTotal = engravingProductPrice + parentProductPrice
    }

    const additionalItemPrice =
      parseInt(
        this.els.pawInput?.elements?.find((el) => el.checked)?.dataset
          .itemPrice,
      ) || 0

    if (additionalItemPrice) {
      priceTotal += additionalItemPrice
    }

    const stonesPrice = parseInt(
      this.els.price.element.dataset.stonesPrice || 0,
    )
    if (stonesPrice) {
      priceTotal += stonesPrice
    }

    const hoopsPrice = parseInt(
      this.els.price.element.dataset.addHoopsPrice || 0,
    )
    if (hoopsPrice) {
      priceTotal += hoopsPrice
    }

    const charmsPrice = parseInt(
      this.els.price.element.dataset.addCharmsPrice || 0,
    )
    if (charmsPrice) {
      priceTotal += charmsPrice
    }

    const tab = this.externalEls.price?.elements.find(
      (el) => el.dataset.engravingType === this.dataset.engravingType,
    )

    if (tab) {
      tab.dataset.personalisationPrice = priceTotal
      tab.dataset.priceStyled = formatMoney(priceTotal)
      tab.dataset.loyaltyPriceStyled = formatMoney(priceTotal)
      tab.dataset.personalisationPriceStyled = formatMoney(priceTotal)

      if (!this.isServiceProduct) {
        tab.querySelector('[data-price]').innerText =
          `+ ${formatMoney(priceDifference)}`
      }

      if (tab.getAttribute('aria-checked') === 'true') {
        tab.click()
      }
    }

    this.els.price?.elements.forEach((el) => {
      el.dataset.price = priceTotal

      const dataPriceEl = el.querySelector('[data-price]')
      if (dataPriceEl) {
        if (stonesPrice) {
          dataPriceEl.dataset.originalPrice = priceTotal - stonesPrice
        }

        dataPriceEl.dataset.price = priceTotal
        dataPriceEl.innerText = formatMoney(priceTotal)
      }
    })
  }

  handleErrors () {
    setTimeout(() => {
      if (this.uploadError) {
        this.uploadError.setAttribute('aria-hidden', true)
      }
    }, 500)
  }

  updateAddToCartButton () {
    const productAvailable =
      this.querySelector('[data-product-form]').dataset.productAvailable ===
      'true'

    const productHandle = this.isServiceProduct
      ? this.parentProductProps.handle
      : this.productProps.handle

    const atcButton = this.els.addToCart.element
    const bisButton = this.els.backInStock.element

    atcButton.ariaHidden = !productAvailable

    bisButton.ariaHidden = productAvailable
    bisButton.dataset.productUrl = `/products/${productHandle}?variant=${this.els.variantId.element.value}`
  }

  handleATCClick (e) {
    const visibleError = this.querySelector(
      '[data-personalisation-form-el="error"][aria-hidden="false"]',
    )

    if (visibleError) {
      this.scrollToElement(visibleError)
      e.preventDefault()
      return false
    }

    const textInputs = this.els.textInput?.elements

    if (textInputs?.length) {
      const hasAnyValue = textInputs.some(
        (input) => input.value.trim().length > 0,
      )

      if (!hasAnyValue) {
        const valueError = this.els.valueError.element
        valueError.setAttribute('aria-hidden', false)
        this.scrollToElement(valueError)
        e.preventDefault()
        return false
      }
    }

    const hasUploadTabs = this.hasAttribute('data-print-engraving')

    const fileInput = this.querySelector(
      '[data-personalisation-file-upload-el="input"]',
    )

    if (fileInput && fileInput.checkVisibility() && !hasUploadTabs) {
      const hasFileValue =
        fileInput && fileInput.files && fileInput.files.length > 0

      if (!hasFileValue) {
        this.uploadError.setAttribute('aria-hidden', false)
        this.scrollToElement(this.uploadError)

        e.preventDefault()
        return false
      }
    }

    if (hasUploadTabs) {
      const pawPrintProductButtons = this.els.pawInput?.elements

      if (pawPrintProductButtons && pawPrintProductButtons[0]?.checked) {
        const additionalInputs = this.els.uploadChoice?.elements

        if (additionalInputs[1]?.checked) {
          const fileInput = this.querySelector(
            '[data-personalisation-file-upload-el="input"]',
          )
          const hasFileValue =
            fileInput && fileInput.files && fileInput.files.length > 0

          if (!hasFileValue) {
            this.uploadError.setAttribute('aria-hidden', false)
            this.scrollToElement(this.uploadError)

            e.preventDefault()
            return false
          }
        }
      }
    }

    const termsValid =
      !this.els?.termsCheckbox?.exist || this.els.termsCheckbox.element.checked

    if (!termsValid) {
      const termsError = this.els?.termsError?.element

      if (termsError) {
        termsError.setAttribute('aria-hidden', false)
        this.scrollToElement(termsError)
        e.preventDefault()
        return false
      }
    }
  }

  scrollToElement (error) {
    const yOffset = document.querySelector('.hd-Header').clientHeight
    const y =
      error.parentElement.getBoundingClientRect().top +
      window.scrollY -
      yOffset -
      10
    window.scrollTo({
      top: y,
      behavior: 'smooth',
    })
  }

  handleTermsCheckbox (e) {
    const termsError = this.els.termsError.element
    termsError.setAttribute('aria-hidden', e.target.checked)
  }

  handleViewImageClick (e) {
    e.preventDefault()
    const targetId = e.target.href.split('#')[1]
    if (!targetId) return

    const target = document.getElementById(targetId)
    if (!target) return

    this.scrollToElement(target)
  }

  createOverlayElements () {
    this.primaryProps = this.personalisationProps.find(
      (prop) => prop.primaryImage,
    )

    const propsToRender = this.primaryProps
      ? [this.primaryProps]
      : this.personalisationProps

    propsToRender.forEach((props) => {
      const placementId = `image-${this.dataset.engravingType}-${props.placement}`

      const container = document.getElementById(placementId)

      const canvasContainer = document.createElement('div')
      canvasContainer.classList.add('prd-Personalisation_Overlay')
      canvasContainer.id = `canvas-${this.dataset.engravingType}-${props.placement}`
      container.appendChild(canvasContainer)

      props.containerId = canvasContainer.id
      props.engravingType = this.dataset.engravingType

      const canvas = new EngravingCanvas()
      canvas.render(
        this.primaryProps ? this.personalisationProps : props,
        true,
        !!this.primaryProps,
      )

      props.canvas = canvas
    })
  }

  setPropertyValue (placement, property, value) {
    const personalisationProps = this.personalisationProps.find(
      (props) => props.placement === placement,
    )

    if (!personalisationProps) return

    personalisationProps[property] = value
    this.updatePreviewText(placement, personalisationProps)
    this.updateLimitCounts(placement, personalisationProps)
    this.updateOverlay(personalisationProps)

    if (
      property === 'text' &&
      this.dataset.engravingType === 'hand-engraving'
    ) {
      this.setServiceProductInputs(this.enteredCharacterCount)
      this.updatePrices()
    }
  }

  setServiceProductInputs (count) {
    const input = this.els.serviceVariantId?.element

    if (!input) return

    const serviceVariants = this.productProps?.variants

    if (!serviceVariants) return

    const targetVariant = Math.min(count, serviceVariants.length)

    this.selectedVariant = serviceVariants[Math.max(0, targetVariant - 1)]

    input.value = this.selectedVariant.id
  }

  updatePreviewText (placement, personalisationProps) {
    const hasValue = !!personalisationProps.text?.length
    const mediaHasValue = this.enteredCharacterCount > 0
    const previewText = this.getPlacementInput('previewText', placement)

    if (previewText) {
      const letterSpacing = this.determineLetterSpacing(personalisationProps)

      previewText.ariaHidden = !hasValue
      previewText.dataset.font = personalisationProps.fontFamily
      previewText.style.letterSpacing = `${letterSpacing}em`

      window.setTimeout(() => {
        previewText.innerHTML = personalisationProps.text
          ?.replace(/ /g, '&nbsp;')
          .replace(/(?:\r\n|\r|\n)/g, '<br>')
          .replace(/❤/g, '❤️')

        let fontSize = parseInt(previewText.style.fontSize)

        if (this.isOverflowing(previewText)) {
          while (this.isOverflowing(previewText)) {
            fontSize--
            previewText.style.fontSize = `${fontSize}px`
          }
        } else {
          fontSize = 32
          previewText.style.fontSize = `${fontSize}px`

          while (this.isOverflowing(previewText)) {
            fontSize--
            previewText.style.fontSize = `${fontSize}px`
          }
        }
      })
    }

    const previewTextEmpty = this.getPlacementInput(
      'previewTextEmpty',
      placement,
    )

    if (previewTextEmpty) {
      previewTextEmpty.ariaHidden = hasValue
    }

    // External/global preview state
    const mediaPreviewText = this.externalEls.previewText?.element

    if (mediaPreviewText) {
      mediaPreviewText.ariaHidden = !mediaHasValue
    }

    const mediaPreviewTextEmpty = this.externalEls.previewTextEmpty?.element

    if (mediaPreviewTextEmpty) {
      mediaPreviewTextEmpty.ariaHidden = mediaHasValue
    }
  }

  isOverflowing (element) {
    return element.scrollWidth > element.clientWidth
  }

  async updateOverlay (personalisationProps) {
    if (this.primaryProps) {
      this.primaryProps.canvas.render(this.personalisationProps, false, true)
    } else {
      personalisationProps.canvas.render(personalisationProps, false, false)
    }

    const previewProps = this.primaryProps
      ? this.personalisationProps.find((prop) => prop.primaryImage)
      : this.personalisationProps.find(
        (prop) =>
          (!!prop.text || !!prop.image) &&
            (!prop.hidePreview || prop.canvas.useTempImage),
      )

    let previewInput = this.querySelector('[data-preview-image]')

    if (!previewProps) {
      if (previewInput) {
        previewInput.value = ''
      }
      return
    }

    if (this.previewTimer) {
      window.clearTimeout(this.previewTimer)
    }

    this.previewTimer = window.setTimeout(async () => {
      const preview = await previewProps.canvas.generatePreview()

      if (!previewInput) {
        previewInput = document.createElement('input')
        previewInput.setAttribute('type', 'hidden')
        previewInput.setAttribute('name', '_preview_image')
        previewInput.setAttribute('data-preview-image', '')
        this.querySelector('[data-product-form]').appendChild(previewInput)
      }

      previewInput.value = preview
    }, 500)
  }

  updateLimitCounts (placement, personalisationProps) {
    const characterCount =
      personalisationProps.text?.replaceAll('\n', '').length || 0

    const charCount = this.getPlacementInput('charCount', placement)

    if (charCount) {
      charCount.innerText = characterCount
    }

    const charCountMedia = this.getPlacementExternalInput(
      'charCount',
      placement,
    )

    if (charCountMedia) {
      charCountMedia.innerText = characterCount
    }

    this.externalEls.charCount?.elements?.forEach((el) => {
      el.innerText = this.enteredCharacterCount
    })
  }

  getPlacementInput (input, placement) {
    return this.els[input]?.elements?.find(
      (el) => !!el.closest(`[data-placement="${placement}"]`),
    )
  }

  getPlacementExternalInput (input, placement) {
    return this.externalEls[input]?.elements?.find(
      (el) =>
        !!el.closest(`[data-placement="${placement}"]`) &&
        !!el.closest(`[data-engraving-type="${this.dataset.engravingType}"]`),
    )
  }

  determineLetterSpacing (personalisationProps) {
    const { letterSpacing, reduceLetterSpacingFrom, maxCharacters, text } =
      personalisationProps
    const characterCount = text?.length

    if (letterSpacing === 0 || characterCount <= reduceLetterSpacingFrom) {
      return letterSpacing
    }

    const diff = maxCharacters - reduceLetterSpacingFrom
    const limit = characterCount - reduceLetterSpacingFrom

    return letterSpacing * (1 - limit / diff)
  }

  get enteredCharacterCount () {
    return this.personalisationProps.reduce(
      (prev, curr) => prev + (curr.text?.replace(/ /g, '').length || 0),
      0,
    )
  }
}

class EngravingCanvas {
  static FONT_FAMILIES = {
    Classic: {
      fontFamily: "'Times New Roman', serif",
      fontStyle: 'normal',
    },
    'Classic Italic': {
      fontFamily: "'Times New Roman', serif",
      fontStyle: 'italic',
    },
    Script: {
      fontFamily: "'Vladimir', cursive",
      fontStyle: 'normal',
    },
    'Hand Stamping': {
      fontFamily: "'URWDIN', sans-serif",
      fontStyle: 'normal',
    },
    'Hand Engraving': {
      fontFamily: "'Samantha', cursive",
      fontStyle: 'italic',
    },
  }

  async render (props, firstRender, hasPrimaryProps) {
    const primaryProps = hasPrimaryProps
      ? props.find((prop) => prop.primaryImage)
      : props

    let updateBackgroundImage = false

    if (
      primaryProps.stoneType &&
      primaryProps.stoneType !== this.renderedStone
    ) {
      updateBackgroundImage = true
      this.renderedStone = primaryProps.stoneType
    }

    if (firstRender || updateBackgroundImage) {
      this.container = document.getElementById(primaryProps.containerId)

      this.stage = new window.Konva.Stage({
        container: primaryProps.containerId,
        width: this.containerWidth,
        height: this.containerWidth,
        draggable: false,
      })

      await this.generateBackground(primaryProps)
    }

    if (props.hidePreview && !this.useTempImage) return

    if (this.textLayer) {
      this.textLayer.remove()
    }

    this.textLayer = new window.Konva.Layer()

    if (hasPrimaryProps) {
      if (this.imageLayer) {
        this.imageLayer.remove()
      }

      this.imageLayer = new window.Konva.Layer()

      props.forEach((prop) => {
        this.generateText(prop)

        this.generateImage(prop)
      })
    } else {
      this.generateText(props)

      if (props.image !== this.image) {
        if (this.imageLayer) {
          this.imageLayer.remove()
        }

        this.imageLayer = new window.Konva.Layer()

        this.generateImage(props)
      }
    }
  }

  generatePreview () {
    this.generatingImage = true

    if (!this.stage) return

    const preview = this.stage.toDataURL({ mimeType: 'image/jpeg' })

    this.generatingImage = false

    return preview
  }

  async generateBackground (props) {
    const backgroundLayer = new window.Konva.Layer()

    let imageAttribute = this.useTempImage
      ? props.tempBackgroundImage
      : props.backgroundImage

    if (props.stoneImages && props.stoneImages.length > 0) {
      const stoneType = props.stoneType

      for (const stone of props.stoneImages) {
        const downcasedAlt = stone.alt.toLowerCase()
        if (downcasedAlt.includes(stoneType)) {
          imageAttribute = this.useTempImage
            ? props.tempBackgroundImage
            : stone.url
        }
      }
    }

    const backgroundImg = await this.createImage(imageAttribute)

    const backgroundImage = new window.Konva.Image({
      image: backgroundImg,
      x: 0,
      y: 0,
      width: this.stageWidth,
      height: this.stageWidth,
      draggable: false,
    })

    backgroundLayer.add(backgroundImage)
    this.stage.add(backgroundLayer)
  }

  async generateText (props) {
    if (!props.text) return

    const size = this.stageWidth / 100

    const x = size * props.x
    const y = size * props.y
    const itemWidth = size * props.maxWidth

    const fontSizeModifier = props.fontFamily === 'Script' ? 8.5 : 10
    const fontSize = size * (props.fontSize / fontSizeModifier)

    const textWrapper = new window.Konva.Group({
      x,
      y,
      id: `text-wrapper`,
      clip: {
        x: itemWidth * -0.5,
        y: -200,
        width: itemWidth,
        height: 400,
      },
    })

    const textGroup = new window.Konva.Group({
      id: `text-group`,
    })

    let engravingType = 'laser-engraving'
    if (props.fontFamily === 'Hand Stamping') {
      engravingType = 'hand-stamping'
    } else if (props.fontFamily === 'Hand Engraving') {
      engravingType = 'hand-engraving'
    }

    const engravingStyles = {
      'laser-engraving': {
        shadowText: {
          fill: 'rgba(255, 255, 255, 0.5)',
          offsetX: fontSize * -0.02,
          offsetY: fontSize * -0.02,
        },
        mainText: {
          fill:
            props.productColour === 'Gold'
              ? 'rgba(116, 77, 23, 0.8)'
              : 'rgba(80, 80, 80, 0.9)',
        },
      },
      'hand-engraving': {
        shadowText: {
          fill: 'rgba(255, 255, 255, 0.6)',
          offsetX: fontSize * -0.02,
          offsetY: fontSize * -0.02,
        },
        mainText: {
          fill:
            props.productColour === 'Gold'
              ? 'rgba(116, 77, 23, 0.7)'
              : 'rgba(60, 60, 60, 0.8)',
          strokeWidth: fontSize * 0.008,
          stroke: 'rgba(255, 255, 255, 0.6)',
        },
      },
      'hand-stamping': {
        shadowText: {
          fill: 'rgba(255, 255, 255, 0.5)',
          offsetX: fontSize * -0.05,
          offsetY: fontSize * -0.05,
          shadowColor: 'rgba(255, 255, 255, 0.6)',
          shadowBlur: 4,
        },
        mainText: {
          fill:
            props.productColour === 'Gold'
              ? 'rgba(116, 77, 23, 0.7)'
              : 'rgba(60, 60, 60, 0.65)',
        },
      },
    }

    const textValue = props.text
    const styles = engravingStyles[engravingType]
    const letterSpacing = this.determineLetterSpacing(props)
    const font = {
      ...EngravingCanvas.FONT_FAMILIES[props.fontFamily],
      letterSpacing: fontSize * letterSpacing,
      fontSize,
      rotation: props.rotation,
    }

    if (textValue.includes('❤') || textValue.includes('⭐')) {
      let textWidth = 0

      const textParts = textValue.split(/([❤⭐])/gm)

      textParts.forEach((textValue) => {
        if (!textValue) return

        if (textValue === '❤') {
          textWidth += this.createEmojiElement(
            this.heartPath,
            styles,
            textGroup,
            textWidth,
            fontSize,
            letterSpacing,
            props.rotation,
          )
        } else if (textValue === '⭐') {
          textWidth += this.createEmojiElement(
            this.starPath,
            styles,
            textGroup,
            textWidth,
            fontSize,
            letterSpacing,
            props.rotation,
          )
        } else {
          textWidth += this.createTextElement(
            textValue,
            font,
            styles,
            textGroup,
            textWidth,
          )
        }
      })
    } else {
      this.createTextElement(textValue, font, styles, textGroup, 0)
    }

    textWrapper.add(textGroup)
    this.textLayer.add(textWrapper)
    this.stage.add(this.textLayer)
  }

  async generateImage (props) {
    if (!props.image) return

    const img = await this.createImage(props.image)

    const size = this.stageWidth / 100

    const x = size * props.imageX
    const y = size * props.imageY
    const maxWidth = size * props.imageMaxWidth
    const maxHeight = size * props.imageMaxHeight

    const imageWrapper = new window.Konva.Group({
      x,
      y,
      offsetX: maxWidth / 2,
      offsetY: maxHeight / 2,
      id: `image-wrapper`,
      clip: {
        x: 0,
        y: 0,
        width: maxWidth,
        height: maxHeight,
      },
    })

    const ratio = img.width / img.height

    const imageWidth = ratio >= 1 ? maxWidth : maxWidth * ratio
    const imageHeight = ratio <= 1 ? maxHeight : maxHeight / ratio
    const offsetX = ratio >= 1 ? 0 : (maxWidth - imageWidth) * -0.5
    const offsetY = ratio <= 1 ? 0 : (maxHeight - imageHeight) * -0.5

    const processed = await this.makeTransparentFromCorner(img, 50)

    const image = new window.Konva.Image({
      image: processed,
      x: 0,
      y: 0,
      offsetX,
      offsetY,
      width: imageWidth,
      height: imageHeight,
      rotation: props.imageRotation,
      draggable: false,
      opacity: 0.7,
    })

    imageWrapper.add(image)
    this.imageLayer.add(imageWrapper)
    this.stage.add(this.imageLayer)
  }

  makeTransparentFromCorner (imgEl, tolerance) {
    const canvas = document.createElement('canvas')
    canvas.width = imgEl.width
    canvas.height = imgEl.height

    const ctx = canvas.getContext('2d')
    ctx.filter = 'grayscale(1)'
    ctx.drawImage(imgEl, 0, 0)

    const data = ctx.getImageData(0, 0, canvas.width, canvas.height)

    const [r, g, b] = data.data

    for (let i = 0; i < data.data.length; i += 4) {
      const dr = Math.abs(data.data[i] - r)
      const dg = Math.abs(data.data[i + 1] - g)
      const db = Math.abs(data.data[i + 2] - b)

      if (dr <= tolerance && dg <= tolerance && db <= tolerance) {
        data.data[i + 3] = 0
      }
    }

    ctx.putImageData(data, 0, 0)

    return this.createImage(canvas.toDataURL())
  }

  createImage (url) {
    return new Promise((resolve, reject) => {
      const image = new Image()
      image.crossOrigin = ''
      image.addEventListener('load', () => resolve(image))
      image.addEventListener('error', (error) => reject(error))
      image.src = url
    })
  }

  createTextElement (text, font, styles, container, x) {
    const shadowText = new window.Konva.Text({
      ...font,
      ...styles.shadowText,
      text,
      align: 'center',
      verticalAlign: 'middle',
      x,
      y: 0,
      name: 'shadow-text',
    })

    const mainText = new window.Konva.Text({
      ...font,
      ...styles.mainText,
      text,
      align: 'center',
      verticalAlign: 'middle',
      x,
      y: 0,
      name: 'main-text',
    })

    // Rotate about the centre of the text, not its top-left corner, so the
    // text stays put as it gets wider.
    const halfWidth = mainText.width() / 2
    const halfHeight = mainText.height() / 2;

    [shadowText, mainText].forEach((node) => {
      node.offsetX(halfWidth + (node.offsetX() || 0))
      node.offsetY(halfHeight + (node.offsetY() || 0))
      node.x(x + halfWidth)
      node.y(halfHeight)
    })

    container.add(shadowText)
    container.add(mainText)

    container.offsetY(mainText.height() / 2)
    container.offsetX(container.offsetX() + mainText.width() / 2)

    return mainText.width()
  }

  createEmojiElement (
    path,
    styles,
    container,
    x,
    fontSize,
    letterSpacing,
    rotation,
  ) {
    const scale = fontSize / 70

    const shadow = new window.Konva.Path({
      ...styles.shadowText,
      data: path,
      scaleX: scale,
      scaleY: scale,
      x: x + (fontSize * letterSpacing) / 2,
      rotation,
    })

    const main = new window.Konva.Path({
      ...styles.mainText,
      data: path,
      scaleX: scale,
      scaleY: scale,
      x: x + (fontSize * letterSpacing) / 2,
      rotation,
    })

    container.add(shadow)
    container.add(main)

    const rect = main.getSelfRect()
    const width = rect.width * scale + fontSize * letterSpacing
    container.offsetY(fontSize / 2)
    container.offsetX(container.offsetX() + width / 2)

    return width
  }

  determineLetterSpacing (personalisationProps) {
    const { letterSpacing, reduceLetterSpacingFrom, maxCharacters, text } =
      personalisationProps
    const characterCount = text?.length

    if (letterSpacing === 0 || characterCount <= reduceLetterSpacingFrom) {
      return letterSpacing
    }

    const diff = maxCharacters - reduceLetterSpacingFrom
    const limit = characterCount - reduceLetterSpacingFrom

    return letterSpacing * (1 - limit / diff)
  }

  get stageWidth () {
    return this.stage.width()
  }

  get containerWidth () {
    const containerOuter = this.container.closest('.prd-Media-personalisation')
    const containerInner = this.container.closest('.prd-Media_Personalisation')
    const placementContainer = this.container.closest(
      '.prd-Media_PersonalisationPlacement',
    )

    const containerVisibility = containerInner.getAttribute('aria-hidden')
    const placementVisibility = placementContainer.getAttribute('aria-hidden')

    containerOuter.setAttribute('aria-hidden', 'false')
    containerInner.setAttribute('aria-hidden', 'false')
    placementContainer.setAttribute('aria-hidden', 'false')

    const width = this.container.clientWidth
    containerOuter.setAttribute('aria-hidden', 'true')
    containerInner.setAttribute('aria-hidden', containerVisibility)
    placementContainer.setAttribute('aria-hidden', placementVisibility)

    return width
  }

  get starPath () {
    return 'm 39.162622,48.894002 c -0.436971,0 -0.873941,-0.135063 -1.245233,-0.404661 l -12.905191,-9.37606 -12.905191,9.37606 c -0.742584,0.539725 -1.747882,0.539725 -2.4904659,0 C 8.8739563,47.949616 8.5630453,46.993578 8.8469437,46.120697 L 13.776499,30.949616 0.87077828,21.573557 C 0.12819352,21.033832 -0.18271751,20.077794 0.1011808,19.204913 0.38454946,18.332032 1.1981088,17.74093 2.1160113,17.74093 H 18.067813 L 22.997367,2.5698487 c 0.28337,-0.8728814 1.096928,-1.4639831 2.014831,-1.4639831 0.917903,0 1.731462,0.5911017 2.014831,1.4639831 L 31.956584,17.74093 h 15.951801 c 0.917902,0 1.731462,0.591102 2.014831,1.463983 0.283897,0.872881 -0.02701,1.829449 -0.769598,2.368644 l -12.905191,9.376059 4.929555,15.171081 c 0.283899,0.872881 -0.02701,1.829449 -0.769597,2.368644 -0.371293,0.269598 -0.808263,0.404661 -1.245234,0.404661 z M 8.6366682,21.978218 17.512198,28.426841 c 0.742585,0.539724 1.053496,1.495763 0.769597,2.368644 l -3.38983,10.433793 8.87553,-6.448094 c 0.742584,-0.539724 1.747881,-0.539724 2.490465,0 l 8.875531,6.448094 -3.389831,-10.433793 c -0.283899,-0.872881 0.02701,-1.829449 0.769598,-2.368644 l 8.875529,-6.448623 H 30.418448 c -0.917902,0 -1.731462,-0.591102 -2.01483,-1.463983 L 25.013787,10.080442 21.623426,20.514235 c -0.283368,0.872881 -1.096928,1.463983 -2.014829,1.463983 H 8.6382572 Z'
  }

  get heartPath () {
    return 'm 25.532398,45.536698 c -0.584683,0 -1.166256,-0.231178 -1.59803,-0.685759 C 19.65343,40.343488 15.387006,35.702825 11.253794,31.059052 10.758264,30.502359 10.243038,29.933226 9.7449171,29.382753 6.4457079,25.738332 3.0340196,21.96951 1.0000688,17.214813 0.39672556,15.804421 0.05203206,14.341158 0.00330846,12.982599 -0.0578552,11.282975 0.32104849,9.7507729 1.1296528,8.4279791 1.782238,7.3602068 2.6945095,6.4406785 3.7679836,5.7694333 4.9005479,5.0608679 6.166843,4.6384239 7.5326587,4.5135049 9.3007031,4.3517841 11.111769,4.6990693 12.916616,5.5460304 c 1.890371,0.8873914 3.720616,2.3091873 5.440455,4.2259942 0.130103,0.1451341 0.29286,0.3213684 0.482053,0.5255934 1.349229,1.459116 3.861086,4.176234 6.093559,8.094855 3.634573,-4.182455 8.459245,-7.633536 13.426979,-9.5254632 2.892833,-1.1014642 5.933393,-1.0242321 8.134248,0.2073344 1.779448,0.9957238 2.934301,2.6549178 3.339121,4.7992738 0.794091,4.20267 -1.447713,8.37683 -3.489957,11.543864 -2.645069,4.10263 -6.194116,7.368148 -9.627056,10.525851 -0.376831,0.346249 -0.752624,0.692497 -1.126863,1.038746 -0.988467,0.915381 -1.979525,1.86912 -2.938447,2.791239 -1.817287,1.748348 -3.696773,3.556304 -5.698069,5.244007 -0.412077,0.347803 -0.916937,0.518854 -1.419723,0.518854 z M 8.3744365,8.8810049 c -0.1482441,0 -0.2949333,0.00674 -0.4400674,0.019697 -1.2994686,0.1192171 -2.4092262,0.7842423 -3.0452246,1.8245431 -0.9941686,1.627057 -0.2182195,3.868861 0.1617209,4.756771 1.744201,4.076713 4.9044962,7.567707 7.9606056,10.944148 0.504341,0.557211 1.025787,1.133083 1.533757,1.703771 3.634572,4.083451 7.372812,8.164312 11.133341,12.153426 1.338344,-1.203058 2.643514,-2.458986 3.917584,-3.684333 0.973435,-0.936633 1.980044,-1.904885 2.999611,-2.848775 0.377867,-0.349877 0.757289,-0.699235 1.137748,-1.048594 3.228715,-2.970066 6.567318,-6.041725 8.907087,-9.670597 1.598549,-2.479201 3.368148,-5.667486 2.863289,-8.338472 -0.161721,-0.85681 -0.530776,-1.419723 -1.161073,-1.77271 -1.043929,-0.584165 -2.777245,-0.558248 -4.415187,0.06531 -2.5668,0.977582 -5.181806,2.493715 -7.563042,4.385124 -2.362057,1.876377 -4.440585,4.078787 -6.010625,6.368277 -0.451989,0.658805 -1.22431,1.021122 -2.020474,0.947519 C 23.537841,24.61199 22.844825,24.113348 22.521902,23.382494 20.314827,18.383661 17.13328,14.942428 15.604707,13.288935 15.403592,13.071752 15.22995,12.884115 15.078077,12.7141 12.84664,10.226606 10.485101,8.8810049 8.3754732,8.8810049 Z'
  }

  get useTempImage () {
    const urlParams = new URLSearchParams(window.location.search)
    return urlParams.get('updating') === '1'
  }
}

if (!customElements.get('personalisation-form')) {
  customElements.define('personalisation-form', PersonalisationForm)
}
